import React from "react";
import "./AmpereMeter.css";

interface AmpereMeterProps {
  value: number;
  /** Full-scale reading in amps. */
  max?: number;
  /** Shows "—" and unfilled segments instead of a reading. */
  offline?: boolean;
}

// A 240° arc from lower-left, over the top, to lower-right. pathLength="100"
// lets each piece be drawn as a percentage range with stroke-dasharray.
const ARC = "M 16.7 85 A 50 50 0 1 1 103.3 85";

// Load zones, as on the old gauge: three equal thirds — green, amber, red.
const THIRD = 100 / 3;
const SEGMENTS = [
  { tone: "good", from: 0, to: THIRD },
  { tone: "caution", from: THIRD, to: THIRD * 2 },
  { tone: "bad", from: THIRD * 2, to: 100 },
];
// Equal visible lengths: the two gaps come out of the arc first, then the rest
// is split three ways.
const GAP = 2.4;
const SEG_LEN = (100 - 2 * GAP) / 3;

// Draws the range [from, to] of the arc.
const dash = (from: number, to: number) => `0 ${from} ${Math.max(0, to - from)} 100`;

// Current draw as a segmented arc: each zone shows faintly, and fills in its
// own colour up to the reading. The reading sits in the middle. Sized in em so
// it scales with the page around it.
export const AmpereMeter: React.FC<AmpereMeterProps> = ({ value, max = 60, offline = false }) => {
  const pct = offline ? 0 : Math.max(0, Math.min(100, (value / max) * 100));
  const tone = offline ? "offline" : pct >= THIRD * 2 ? "bad" : pct >= THIRD ? "caution" : "good";

  return (
    <div
      className={`amp-gauge ${tone}`}
      role="meter"
      aria-label="Current draw"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={offline ? undefined : Number(value.toFixed(2))}
      aria-valuetext={offline ? "Offline" : `${value.toFixed(2)} amps of ${max}`}
    >
      <svg className="amp-gauge-arc" viewBox="0 0 120 96" aria-hidden="true">
        {SEGMENTS.map(({ tone }, i) => {
          const start = i * (SEG_LEN + GAP);
          const end = start + SEG_LEN;
          const fill = Math.min(Math.max(pct, start), end);
          return (
            <g key={tone} className={`amp-seg ${tone}`}>
              <path className="amp-seg-track" d={ARC} pathLength={100} strokeDasharray={dash(start, end)} />
              {fill > start && (
                <path className="amp-seg-fill" d={ARC} pathLength={100} strokeDasharray={dash(start, fill)} />
              )}
            </g>
          );
        })}
        {/* Marker at the reading: a tick across the arc, drawn at the arc's start
            (150°) and rotated about the centre to the value */}
        {!offline && (
          <line
            className="amp-gauge-marker"
            x1={60 + 41 * Math.cos((150 * Math.PI) / 180)}
            y1={60 + 41 * Math.sin((150 * Math.PI) / 180)}
            x2={60 + 59 * Math.cos((150 * Math.PI) / 180)}
            y2={60 + 59 * Math.sin((150 * Math.PI) / 180)}
            style={{ transform: `rotate(${(240 * pct) / 100}deg)` }}
          />
        )}
      </svg>
      <div className="amp-gauge-readout">
        <span className="amp-gauge-number">
          {offline ? "—" : value.toFixed(2)}
          {!offline && <span className="amp-gauge-unit">A</span>}
        </span>
      </div>
    </div>
  );
};
